// Free water test landing page (sections/wt-*.liquid)

/**
 * Accessible tabs: <wt-tabs> wrapping [role=tab] buttons and [role=tabpanel] panels.
 * With data-autoplay="5" the tabs advance every 5 seconds while visible. Autoplay pauses on
 * hover or keyboard focus, stops once the visitor picks a tab, and is off with reduced motion.
 */
class WtTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        this.stop();
        this.select(tab);
      });
      tab.addEventListener('keydown', (event) => this.onKeydown(event, tab));
    });

    const seconds = Number(this.dataset.autoplay || 0);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (seconds > 0 && !reduced && this.tabs.length > 1) this.setupAutoplay(seconds * 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.visibility?.disconnect();
  }

  setupAutoplay(delay) {
    this.delay = delay;
    this.inView = false;
    this.paused = false;
    this.stopped = false;
    this.addEventListener('pointerenter', () => this.setPaused(true));
    this.addEventListener('pointerleave', () => this.setPaused(false));
    this.addEventListener('focusin', () => this.setPaused(true));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.setPaused(false);
    });
    this.visibility = new IntersectionObserver(
      ([entry]) => {
        this.inView = entry.isIntersecting;
        this.restart();
      },
      { threshold: 0.35 }
    );
    this.visibility.observe(this);
  }

  setPaused(paused) {
    this.paused = paused;
    this.restart();
  }

  stop() {
    this.stopped = true;
    this.restart();
  }

  restart() {
    clearInterval(this.timer);
    const playing = !!this.delay && this.inView && !this.paused && !this.stopped;
    this.classList.toggle('is-playing', playing);
    if (!playing) return;
    this.restartProgress();
    this.timer = setInterval(() => {
      const index = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.select(this.tabs[(index + 1) % this.tabs.length]);
      this.restartProgress();
    }, this.delay);
  }

  // Re-trigger the CSS progress animation on the active tab.
  restartProgress() {
    this.classList.remove('is-playing');
    void this.offsetWidth;
    this.classList.add('is-playing');
  }

  select(tab, focus = false) {
    this.tabs.forEach((other) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panel = this.querySelector(`#${other.getAttribute('aria-controls')}`);
      if (panel) panel.hidden = !selected;
    });
    if (focus) tab.focus();
    const list = tab.parentElement;
    list.scrollTo({ left: tab.offsetLeft - (list.clientWidth - tab.clientWidth) / 2, behavior: 'smooth' });
  }

  onKeydown(event, tab) {
    const index = this.tabs.indexOf(tab);
    const last = this.tabs.length - 1;
    const next = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: last }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    this.stop();
    this.select(this.tabs[next > last ? 0 : next < 0 ? last : next], true);
  }
}

if (!customElements.get('wt-tabs')) customElements.define('wt-tabs', WtTabs);

// Theme editor: show the region panel of the block being edited.
document.addEventListener('shopify:block:select', (event) => {
  const tab = document.getElementById(`WtTab-${event.detail.blockId}`);
  const tabs = tab?.closest('wt-tabs');
  if (tabs) {
    tabs.stop?.();
    tabs.select(tab);
  }
});

/**
 * Lead form (snippets/wt-booking-form.liquid): free water test or product consultation.
 * Posts urlencoded data to the Google Apps Script web app and reads its JSON reply,
 * so a failed submission is shown to the visitor instead of being lost.
 */
class WtBooking extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.type = this.dataset.formType || 'water_test';
    this.steps = [...this.querySelectorAll('[data-wt-step]')];
    this.step = 1;
    this.errorBox = this.querySelector('[data-wt-error]');
    this.zipHint = this.querySelector('[data-wt-zip-hint]');
    this.prefixes = (this.dataset.zipPrefixes || '')
      .split(',')
      .map((prefix) => prefix.trim())
      .filter(Boolean);

    this.querySelectorAll('[data-wt-next]').forEach((button) => button.addEventListener('click', () => this.next()));
    this.querySelectorAll('[data-wt-back]').forEach((button) => button.addEventListener('click', () => this.goTo(this.step - 1)));
    this.form.addEventListener('submit', (event) => this.submit(event));
    this.form.addEventListener('input', (event) => this.clearInvalid(event.target));
    this.form.addEventListener('change', (event) => this.clearInvalid(event.target));

    const zip = this.form.elements.zip;
    zip?.addEventListener('input', () => {
      zip.value = zip.value.replace(/\D/g, '').slice(0, 5);
      this.checkZip();
    });

    this.renderDates();

    // Coming from a link with ?product=: preselect it in the product picker.
    const interest = new URLSearchParams(location.search).get('product');
    if (interest) this.selectProduct(interest);
  }

  /** Dates in Pacific Time: the next valid days from tomorrow, skipping Sundays. */
  availableDates() {
    const count = Number(this.dataset.days) || 6;
    const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles' }).format(new Date());
    const cursor = new Date(`${today}T12:00:00Z`);
    const dates = [];
    while (dates.length < count) {
      cursor.setUTCDate(cursor.getUTCDate() + 1);
      if (cursor.getUTCDay() === 0) continue;
      const value = cursor.toISOString().slice(0, 10);
      const label = cursor.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
      dates.push({ value, label });
    }
    return dates;
  }

  renderDates() {
    const dates = this.availableDates();
    this.querySelectorAll('[data-wt-dates]').forEach((target) => {
      if (target.dataset.wtDates === 'select') {
        dates.forEach(({ value, label }) => target.add(new Option(label, value)));
        return;
      }
      target.innerHTML = dates
        .map(({ value, label }) => {
          const [day, rest] = label.split(', ');
          return `<label class="wt-choice wt-choice--date"><input type="radio" name="preferred_date" value="${value}"><span>${day}</span><strong>${rest}</strong></label>`;
        })
        .join('');
    });
  }

  selectProduct(name) {
    const picker = this.querySelector('wt-multi');
    if (picker) picker.select(name);
  }

  checkZip() {
    const value = this.form.elements.zip.value;
    if (!this.zipHint) return;
    if (value.length < 5 || this.prefixes.length === 0) {
      this.zipHint.textContent = '';
      delete this.zipHint.dataset.state;
      return;
    }
    const served = this.prefixes.some((prefix) => value.startsWith(prefix));
    this.zipHint.dataset.state = served ? 'ok' : 'warn';
    this.zipHint.textContent = served
      ? 'Great, we serve your area'
      : "We may not serve this ZIP yet. Send your request and we'll confirm";
  }

  goTo(step) {
    if (step < 1) return;
    this.step = step;
    this.steps.forEach((panel) => {
      panel.hidden = Number(panel.dataset.wtStep) !== step;
    });
    this.querySelectorAll('[data-wt-progress]').forEach((item) => {
      const index = Number(item.dataset.wtProgress);
      if (index < step) item.dataset.state = 'done';
      else if (index === step) item.dataset.state = 'active';
      else delete item.dataset.state;
    });
    if (step === this.steps.length && this.type === 'water_test') this.renderReview();
    const panel = this.steps.find((candidate) => Number(candidate.dataset.wtStep) === step);
    panel?.querySelector('input:not([type="hidden"]), select, textarea')?.focus({ preventScroll: true });
    if (this.getBoundingClientRect().top < 0) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** Returns an error message for a field, or '' when it is valid. */
  fieldError(field) {
    const value = field.value.trim();
    const message = field.dataset.wtMsg || 'Please check this field';
    if (field.type === 'checkbox') return field.required && !field.checked ? message : '';
    if (field.required && !value) return message;
    if (!value) return '';
    if (field.name === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return message;
    if (field.name === 'phone') {
      const digits = value.replace(/\D/g, '').length;
      if (!/^[+\d\s().-]+$/.test(value) || digits < 10 || digits > 15) return message;
    }
    if (field.name === 'zip' && !/^\d{5}$/.test(value)) return message;
    return '';
  }

  showError(name, message, target) {
    const slot = this.querySelector(`[data-wt-error-for="${name}"]`);
    if (slot) slot.textContent = message;
    if (target) target.setAttribute('aria-invalid', String(Boolean(message)));
  }

  validate(panel) {
    let firstInvalid = null;
    panel.querySelectorAll('input[name], select[name], textarea[name]').forEach((field) => {
      if (field.closest('[hidden]') || field.closest('[data-wt-required-group]') || field.name === 'website') return;
      const message = this.fieldError(field);
      this.showError(field.name, message, field);
      if (message && !firstInvalid) firstInvalid = field;
    });
    panel.querySelectorAll('[data-wt-required-group]').forEach((group) => {
      const valid = !!group.querySelector('input:checked');
      const message = valid ? '' : group.dataset.wtMsg || 'Please choose an option';
      this.showError(group.dataset.wtName, message, group);
      if (!valid && !firstInvalid) firstInvalid = group.querySelector('button, input');
    });
    firstInvalid?.focus();
    return !firstInvalid;
  }

  clearInvalid(field) {
    if (!field?.name) return;
    const group = field.closest('[data-wt-required-group]');
    if (group) {
      group.removeAttribute('aria-invalid');
      this.showError(group.dataset.wtName, '');
      return;
    }
    if (field.getAttribute('aria-invalid') === 'true' && !this.fieldError(field)) this.showError(field.name, '', field);
  }

  next() {
    const panel = this.steps.find((candidate) => Number(candidate.dataset.wtStep) === this.step);
    if (this.validate(panel)) this.goTo(this.step + 1);
  }

  values() {
    const data = new FormData(this.form);
    const value = (name) => String(data.get(name) || '').trim();
    const dateValue = value('preferred_date');
    const date = dateValue
      ? new Date(`${dateValue}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
      : '';
    const time = value('preferred_time') ? `${value('preferred_time')} PT` : '';
    return {
      data,
      value,
      when: [date, time].filter(Boolean).join(' · '),
      address: [value('address'), value('city'), value('zip')].filter(Boolean).join(', '),
    };
  }

  renderReview() {
    const target = this.querySelector('[data-wt-review]');
    if (!target) return;
    const { data, value, when, address } = this.values();
    const rows = [
      ['Name', value('full_name')],
      ['Email', value('email')],
      ['Phone', value('phone')],
      ['Address', address],
      ['Water source', value('water_source')],
      ['Help with', data.getAll('concerns').join(', ')],
      ['Preferred time', when],
    ];
    target.innerHTML = rows
      .filter(([, text]) => text)
      .map(([label, text]) => `<div><dt>${label}</dt><dd></dd></div>`)
      .join('');
    // Set text separately so visitor input is never parsed as HTML.
    const filled = rows.filter(([, text]) => text);
    target.querySelectorAll('dd').forEach((dd, i) => {
      dd.textContent = filled[i][1];
    });
  }

  payload() {
    const { data, value } = this.values();
    const params = new URLSearchParams(location.search);
    const fullName = value('full_name');
    const body = new URLSearchParams({
      name: fullName,
      first_name: fullName.split(/\s+/)[0] || '',
      phone: value('phone'),
      email: value('email'),
      // ZIP is optional; 'Not provided' keeps older script versions (which require it) accepting the lead.
      zip: value('zip') || 'Not provided',
      address: value('address'),
      city: value('city'),
      water_source: value('water_source'),
      appointment_type: { consultation: 'Product consultation', contact: 'Contact message' }[this.type] || 'Free water test',
      preferred_date: value('preferred_date'),
      preferred_time: value('preferred_time') ? `${value('preferred_time')} PT` : '',
      product: data.getAll('product').join('; '),
      concerns: data.getAll('concerns').join('; '),
      notes: [value('topic') && `Topic: ${value('topic')}`, value('notes')].filter(Boolean).join('\n'),
      consent: value('consent') || 'No',
      website: value('website'),
      token: this.dataset.token || '',
      form_type: this.type,
      lead_source: this.dataset.leadSource || '',
      page_url: location.href,
      referrer: document.referrer,
    });
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid'].forEach((key) => {
      body.set(key, params.get(key) || '');
    });
    return body;
  }

  async submit(event) {
    event.preventDefault();
    // Check every step again in case a field was changed after moving on.
    for (const panel of this.steps) {
      if (!this.validate(panel)) {
        this.goTo(Number(panel.dataset.wtStep));
        this.validate(panel);
        return;
      }
    }

    const button = this.querySelector('[data-wt-submit]');
    const label = button.innerHTML;
    const endpoint = this.dataset.endpoint;
    const body = this.payload();
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Sending…';
    this.errorBox.hidden = true;

    try {
      if (!endpoint) throw new Error('Missing endpoint');
      const response = await fetch(endpoint, { method: 'POST', body });
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || 'Rejected');
      this.showSuccess(body, result.id);
    } catch (error) {
      console.error('[wt-booking]', error);
      this.errorBox.innerHTML = this.querySelector('[data-wt-error-template]').innerHTML;
      this.errorBox.hidden = false;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.innerHTML = label;
    }
  }

  showSuccess(body, leadId) {
    const { when, address } = this.values();
    const output = {
      first_name: body.get('first_name') ? `, ${body.get('first_name')}` : '',
      product: body.get('product').replace(/; /g, ', '),
      when: when || "We'll call to schedule",
      address,
    };
    this.querySelectorAll('[data-wt-out]').forEach((node) => {
      node.textContent = output[node.dataset.wtOut] || '';
      const row = node.closest('div');
      if (row && node.tagName === 'DD') row.hidden = !output[node.dataset.wtOut];
    });

    this.querySelector('[data-wt-form-body]').hidden = true;
    const success = this.querySelector('[data-wt-success]');
    success.hidden = false;
    success.focus({ preventScroll: true });
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Conversion events for analytics and ad pixels, when present on the page.
    const detail = { lead_id: leadId, form_type: this.type, zip: body.get('zip') };
    window.dataLayer?.push({ event: 'generate_lead', form: this.type, ...detail });
    window.fbq?.('track', 'Lead', { content_name: { consultation: 'Product consultation', contact: 'Contact message' }[this.type] || 'Free water test' });
    window.Shopify?.analytics?.publish?.('wt_lead_submitted', detail);
  }
}

if (!customElements.get('wt-booking')) customElements.define('wt-booking', WtBooking);

/**
 * Multi-select product picker (<wt-multi>): a dropdown of checkboxes with removable chips.
 * "Not sure — help me choose" and specific products exclude each other.
 */
class WtMulti extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.wt-multi__toggle');
    this.list = this.querySelector('.wt-multi__list');
    this.label = this.querySelector('[data-wt-multi-label]');
    this.chips = this.querySelector('[data-wt-multi-chips]');
    this.notSure = this.dataset.notSure;
    this.inputs = [...this.querySelectorAll('input[type="checkbox"]')];

    this.toggle.addEventListener('click', () => this.setOpen(this.list.hidden));
    this.addEventListener('change', (event) => this.onChange(event.target));
    this.chips.addEventListener('click', (event) => {
      const button = event.target.closest('[data-value]');
      if (!button) return;
      const input = this.inputs.find((candidate) => candidate.value === button.dataset.value);
      if (input) {
        input.checked = false;
        this.onChange(input);
      }
    });
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.setOpen(false);
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.list.hidden) {
        this.setOpen(false);
        this.toggle.focus();
      }
    });
    this.render();
  }

  setOpen(open) {
    this.list.hidden = !open;
    this.toggle.setAttribute('aria-expanded', String(open));
  }

  onChange(input) {
    if (input.checked) {
      if (input.value === this.notSure) {
        this.inputs.forEach((other) => {
          if (other !== input) other.checked = false;
        });
      } else {
        this.inputs.forEach((other) => {
          if (other.value === this.notSure) other.checked = false;
        });
      }
    }
    this.render();
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /** Selects the option whose model appears in a product title or model, e.g. from a product card. */
  select(name) {
    const target = String(name).toUpperCase();
    const match = this.inputs.find((input) => {
      if (input.value === this.notSure) return false;
      const model = input.value.split('—')[0].trim().toUpperCase();
      return target === input.value.toUpperCase() || target.includes(model) || model.includes(target) || model.endsWith(target.split(' ').slice(-2).join(' '));
    });
    if (!match) return;
    match.checked = true;
    this.onChange(match);
  }

  render() {
    const selected = this.inputs.filter((input) => input.checked);
    this.label.textContent = selected.length === 0
      ? 'Select products'
      : selected.length === 1
        ? selected[0].value
        : `${selected.length} products selected`;
    this.chips.innerHTML = '';
    selected.forEach((input) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.value = input.value;
      button.setAttribute('aria-label', `Remove ${input.value}`);
      button.textContent = '×';
      const text = document.createElement('span');
      text.textContent = input.value.split('—')[0].trim();
      item.append(text, button);
      this.chips.append(item);
    });
  }
}

if (!customElements.get('wt-multi')) customElements.define('wt-multi', WtMulti);

/** Concern picker (sections/wt-concerns.liquid): mirrors selections into the booking form. */
class WtConcerns extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-wt-concern]').forEach((button) => {
      button.addEventListener('click', () => {
        const pressed = button.getAttribute('aria-pressed') !== 'true';
        button.setAttribute('aria-pressed', String(pressed));
        this.sync(button.dataset.wtConcern, pressed);
      });
    });
  }

  sync(value, checked) {
    document.querySelectorAll('wt-booking input[name="concerns"]').forEach((input) => {
      if (input.value.trim().toLowerCase() === value.trim().toLowerCase()) input.checked = checked;
    });
  }
}

if (!customElements.get('wt-concerns')) customElements.define('wt-concerns', WtConcerns);

/**
 * Scroll reveal for the landing sections. Content stays visible without JS, in the theme
 * editor and with reduced motion; only elements below the fold are animated in.
 */
const revealSelector = [
  '.wt .wt-head',
  '.wt .wt-head-row',
  '.wt .wt-card',
  '.wt .wt-step',
  '.wt .wt-concerns li',
  '.wt .wt-media',
  '.wt .wt-review',
  '.wt .wt-faq__item',
  '.wt .wt-strip',
  '.wt .wt-benefits li',
  '.wt .wt-phone-card',
  '.wt .wt-cta__box',
  '.wt .wt-tabs',
  '.wt-booking .wt-form',
  '.wt .k-post',
  '.wt .k-collection-card',
  '.wt .k-aplus__text',
  '.wt .k-aplus__media',
  '.wt .k-aplus__item',
  '.wt .k-aplus__group',
  '.wt .k-timeline__item',
  '.wt .k-stats > div',
  '.wt .k-track',
  '.wt .k-overview__banner',
  '.wt .k-overview__highlights',
  '.wt .wt-panel__body',
  '.wt .wt-split > *',
].join(',');

// Large photos open like a curtain instead of sliding up.
const revealMediaSelector = '.wt .wt-media, .wt .k-aplus__media, .wt .k-overview__banner';

function initReveal() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced || !('IntersectionObserver' in window)) return;

  // Items inside a horizontal slider or a hidden tab may never scroll into view, so skip them.
  const targets = [...document.querySelectorAll(revealSelector)].filter(
    (el) =>
      !el.closest('.wt-hero') &&
      !el.hasAttribute('data-wt-reveal') &&
      !el.parentElement.closest('[data-k-track], [hidden]') &&
      el.getClientRects().length > 0
  );
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        el.classList.add('is-visible');
        observer.unobserve(el);
        // Drop the reveal state afterwards so hover transitions are not delayed.
        el.addEventListener(
          'transitionend',
          () => {
            el.removeAttribute('data-wt-reveal');
            el.classList.remove('is-visible');
            el.style.removeProperty('--wt-i');
          },
          { once: true }
        );
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  targets.forEach((el) => {
    if (el.getBoundingClientRect().top < window.innerHeight * 0.92) return; // already on screen
    const siblings = [...el.parentElement.children].filter((child) => child.matches(revealSelector));
    el.style.setProperty('--wt-i', String(Math.min(siblings.indexOf(el), 6)));
    el.setAttribute('data-wt-reveal', el.matches(revealMediaSelector) ? 'media' : '');
    observer.observe(el);
  });

  initCountUp();
}

/** Stats such as "20+", "50+" or "7,200" count up from zero when they scroll into view. */
function initCountUp() {
  const numbers = [...document.querySelectorAll('.wt .k-stats dt, .wt [data-wt-count]')].filter((el) => !el.dataset.wtCounted);
  if (!numbers.length) return;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        countUp(entry.target);
      });
    },
    { threshold: 0.6 }
  );
  numbers.forEach((el) => {
    const match = el.textContent.trim().match(/^(\D*)([\d,]+)(.*)$/);
    if (!match) return;
    const target = Number(match[2].replace(/,/g, ''));
    if (!(target >= 10)) return;
    el.dataset.wtCounted = 'true';
    el.dataset.wtPrefix = match[1];
    el.dataset.wtTarget = String(target);
    el.dataset.wtSuffix = match[3];
    el.dataset.wtComma = match[2].includes(',') ? 'true' : '';
    // Reserve the final width so the layout does not shift while counting.
    el.style.minWidth = `${el.getBoundingClientRect().width}px`;
    observer.observe(el);
  });
}

function countUp(el) {
  const target = Number(el.dataset.wtTarget);
  const format = (value) => (el.dataset.wtComma ? value.toLocaleString('en-US') : String(value));
  const duration = 1400;
  const start = performance.now();
  const tick = (now) => {
    const progress = Math.min((now - start) / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    el.textContent = `${el.dataset.wtPrefix}${format(Math.round(target * eased))}${el.dataset.wtSuffix}`;
    if (progress < 1) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initReveal, { once: true });
else initReveal();

// Theme editor: animate re-rendered sections and never leave a selected section hidden.
document.addEventListener('shopify:section:load', initReveal);
document.addEventListener('shopify:section:select', (event) => {
  event.target.querySelectorAll('[data-wt-reveal]').forEach((el) => el.classList.add('is-visible'));
});

/**
 * Horizontal slider (<k-slider>): a scroll-snap track with prev/next buttons, dots and an
 * optional autoplay (data-autoplay seconds) that pauses on hover/focus and stops on interaction.
 */
class KSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-k-track]');
    this.slides = [...this.track.children];
    this.dots = this.querySelector('[data-k-dots]');
    if (this.slides.length < 2) return;

    this.querySelector('[data-k-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
    this.querySelector('[data-k-next]')?.addEventListener('click', () => this.go(this.index + 1, true));

    if (this.dots) {
      this.dots.innerHTML = this.slides
        .map((_, i) => `<button type="button" aria-label="Go to slide ${i + 1}"></button>`)
        .join('');
      [...this.dots.children].forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));
    }

    this.index = 0;
    this.track.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.syncIndex(), 80);
    }, { passive: true });
    this.track.addEventListener('pointerdown', () => this.stop(), { passive: true });
    this.track.addEventListener('wheel', (event) => Math.abs(event.deltaX) > 4 && this.stop(), { passive: true });
    this.syncIndex();

    const seconds = Number(this.dataset.autoplay || 0);
    if (seconds > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.delay = seconds * 1000;
      this.addEventListener('pointerenter', () => { this.hovered = true; this.schedule(); });
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); });
      this.addEventListener('focusin', () => { this.hovered = true; this.schedule(); });
      this.addEventListener('focusout', () => { this.hovered = false; this.schedule(); });
      new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.schedule();
      }, { threshold: 0.4 }).observe(this);
    }
  }

  go(index, byUser = false) {
    if (byUser) this.stop();
    const count = this.slides.length;
    this.index = (index + count) % count;
    const slide = this.slides[this.index];
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    this.update();
  }

  syncIndex() {
    const left = this.track.scrollLeft;
    let best = 0;
    this.slides.forEach((slide, i) => {
      if (Math.abs(slide.offsetLeft - this.track.offsetLeft - left) < Math.abs(this.slides[best].offsetLeft - this.track.offsetLeft - left)) best = i;
    });
    this.index = best;
    this.update();
  }

  update() {
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
    if (this.dots) [...this.dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
  }

  stop() {
    this.stopped = true;
    clearInterval(this.timer);
  }

  schedule() {
    clearInterval(this.timer);
    if (!this.delay || this.stopped || this.hovered || !this.inView) return;
    this.timer = setInterval(() => this.go(this.index + 1), this.delay);
  }
}

if (!customElements.get('k-slider')) customElements.define('k-slider', KSlider);

/** Category filter (<k-filter>): tabs with data-k-filter show matching [data-category] cards. */
class KFilter extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-k-filter]')];
    this.cards = [...this.querySelectorAll('[data-category]')];
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.apply(tab)));
    if (this.tabs[0]) this.apply(this.tabs[0]);
  }

  apply(tab) {
    const key = tab.dataset.kFilter;
    this.tabs.forEach((other) => other.setAttribute('aria-selected', String(other === tab)));
    const seen = new Set();
    this.cards.forEach((card) => {
      // "All" shows the first product of each category, once even if it sits in several.
      const id = card.querySelector('a')?.getAttribute('href');
      const match = key === 'all' ? card.hasAttribute('data-first') && !seen.has(id) : card.dataset.category === key;
      if (key === 'all' && match) seen.add(id);
      const item = card.closest('.k-grid__item') || card;
      item.hidden = !match;
      if (match) {
        card.classList.remove('k-card--in');
        void card.offsetWidth;
        card.classList.add('k-card--in');
      }
    });
  }
}

if (!customElements.get('k-filter')) customElements.define('k-filter', KFilter);

document.addEventListener('shopify:block:select', (event) => {
  const slider = event.target.closest('k-slider');
  if (slider?.slides) slider.go(slider.slides.indexOf(event.target), true);
});

/** World map (<k-map>): starts the arc animation the first time it scrolls into view. */
class KMap extends HTMLElement {
  connectedCallback() {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      this.classList.add('is-live');
      this.querySelectorAll('animateMotion').forEach((motion) => motion.remove());
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.classList.add('is-live');
      observer.disconnect();
    }, { threshold: 0.3 });
    observer.observe(this);
  }
}

if (!customElements.get('k-map')) customElements.define('k-map', KMap);

// Product cards with an in-page lead link: preselect the product in the booking form.
document.addEventListener('click', (event) => {
  const link = event.target.closest('a[data-k-product]');
  if (!link || !link.getAttribute('href')?.startsWith('#')) return;
  const target = document.querySelector(link.getAttribute('href'));
  target?.closest('wt-booking, .wt-section')?.querySelector('wt-multi')?.select(link.dataset.kProduct);
});

// Collection sort: reload with ?sort_by= and keep the grid in view.
document.addEventListener('change', (event) => {
  if (!event.target.matches('[data-k-sort]')) return;
  const url = new URL(location.href);
  url.searchParams.set('sort_by', event.target.value);
  url.searchParams.delete('page');
  url.hash = 'products';
  location.href = url.toString();
});

/** Product gallery (<k-gallery>): thumbnails, arrows, keyboard and swipe. */
class KGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-k-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-k-thumb]')];
    if (this.slides.length < 2) return;
    this.index = 0;
    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.show(i)));
    this.querySelector('[data-k-prev]')?.addEventListener('click', () => this.show(this.index - 1));
    this.querySelector('[data-k-next]')?.addEventListener('click', () => this.show(this.index + 1));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.show(this.index + 1);
      if (event.key === 'ArrowLeft') this.show(this.index - 1);
    });
    let startX = null;
    const stage = this.querySelector('.k-gallery__stage');
    stage.addEventListener('pointerdown', (event) => { startX = event.clientX; });
    stage.addEventListener('pointerup', (event) => {
      if (startX === null) return;
      const delta = event.clientX - startX;
      if (Math.abs(delta) > 40) this.show(this.index + (delta < 0 ? 1 : -1));
      startX = null;
    });
  }

  show(index) {
    const count = this.slides.length;
    this.index = (index + count) % count;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-selected', String(i === this.index)));
    this.thumbs[this.index]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }
}

if (!customElements.get('k-gallery')) customElements.define('k-gallery', KGallery);

/**
 * In-page navigation (<k-anchors>): pins below the site header once scrolled past, and
 * marks the section being read. JS-based because the Shopify section wrapper prevents
 * position: sticky from lasting beyond the product's first section.
 */
class KAnchors extends HTMLElement {
  connectedCallback() {
    this.nav = this.querySelector('nav');
    this.links = [...this.nav.querySelectorAll('a[href^="#"]:not(.k-anchors__cta)')];
    this.targets = this.links.map((link) => document.querySelector(link.getAttribute('href'))).filter(Boolean);
    this.header = document.querySelector('.k-header-section') || document.querySelector('sticky-header');
    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    // The header shrinks with a transition after scrolling; re-measure when it settles.
    this.header?.addEventListener('transitionend', this.onScroll);
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  update() {
    clearTimeout(this.settle);
    this.settle = setTimeout(() => this.measure(), 380);
    this.measure();
  }

  measure() {
    const headerBottom = this.header ? Math.max(0, this.header.getBoundingClientRect().bottom) : 0;
    const fixed = this.getBoundingClientRect().top < headerBottom;
    this.nav.classList.toggle('is-fixed', fixed);
    this.nav.style.setProperty('--k-anchors-top', `${headerBottom}px`);

    const line = headerBottom + this.nav.offsetHeight + 24;
    let current = null;
    this.targets.forEach((target, i) => {
      if (target.getBoundingClientRect().top <= line) current = this.links[i];
    });
    this.links.forEach((link) => link.classList.toggle('is-current', link === current));
  }
}

if (!customElements.get('k-anchors')) customElements.define('k-anchors', KAnchors);
